import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CircleNotch } from "@phosphor-icons/react";
import { SEO } from "../components/site/SEO";
import { PageHeader } from "../components/site/primitives";
import { API, money, useCart, shippingFor } from "../cart";

const METHODS = ["Standard", "Express"];

export default function Checkout() {
  const { items, subtotal } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ name: "", email: "", phone: "", billing_address: "", delivery_address: "", delivery_method: "Standard", diff: false });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const ship = shippingFor(f.delivery_method, subtotal);
  const total = subtotal + ship;
  const vat = total - total / 1.2;

  if (items.length === 0) {
    return <main className="pt-40 px-6 text-center"><p className="font-serif text-3xl">Your basket is empty.</p><button onClick={() => navigate("/shop")} className="mt-6 font-mono text-xs underline">Browse the shop</button></main>;
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.billing_address) { toast.error("Please complete name, email and billing address."); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/checkout`, {
        items: items.map((i) => ({ product_id: i.id, quantity: i.qty })),
        customer: {
          name: f.name, email: f.email, phone: f.phone,
          billing_address: f.billing_address,
          delivery_address: f.diff ? f.delivery_address : f.billing_address,
          delivery_method: f.delivery_method,
        },
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed. Please try again.");
      setLoading(false);
    }
  };

  const input = "mt-2 w-full bg-transparent border-b border-black/20 py-3 text-base focus:border-[var(--text-primary)] outline-none transition-colors";
  const label = "font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--text-muted)]";

  return (
    <>
      <SEO title="Secure Checkout | Emporium London" description="Secure guest checkout with card, Apple Pay and Google Pay via Stripe." path="/checkout" />
      <main>
        <PageHeader overline="Secure Checkout" title="Checkout" />
        <section className="relative z-10 px-6 md:px-12 lg:px-16 py-16 md:py-20 grid grid-cols-1 lg:grid-cols-5 gap-12">
          <form data-testid="checkout-form" onSubmit={submit} className="lg:col-span-3 space-y-8">
            <div><label className={label}>Full name *</label><input data-testid="co-name" className={input} value={f.name} onChange={(e) => set("name", e.target.value)} /></div>
            <div className="grid sm:grid-cols-2 gap-6">
              <div><label className={label}>Email *</label><input data-testid="co-email" type="email" className={input} value={f.email} onChange={(e) => set("email", e.target.value)} /></div>
              <div><label className={label}>Telephone</label><input data-testid="co-phone" className={input} value={f.phone} onChange={(e) => set("phone", e.target.value)} /></div>
            </div>
            <div><label className={label}>Billing address *</label><textarea data-testid="co-billing" rows={2} className={input + " resize-none"} value={f.billing_address} onChange={(e) => set("billing_address", e.target.value)} /></div>
            <label className="flex items-center gap-2 font-mono text-[11px] tracking-[0.1em] uppercase text-[var(--text-secondary)]">
              <input data-testid="co-diff" type="checkbox" checked={f.diff} onChange={(e) => set("diff", e.target.checked)} /> Deliver to a different address
            </label>
            {f.diff && <div><label className={label}>Delivery address</label><textarea data-testid="co-delivery" rows={2} className={input + " resize-none"} value={f.delivery_address} onChange={(e) => set("delivery_address", e.target.value)} /></div>}
            <div>
              <label className={label}>Delivery method</label>
              <div className="mt-3 flex gap-2">
                {METHODS.map((m) => (
                  <button key={m} type="button" data-testid={`co-method-${m}`} onClick={() => set("delivery_method", m)}
                    className={`border px-4 py-3 font-mono text-[10px] tracking-[0.12em] uppercase ${f.delivery_method === m ? "bg-[var(--text-primary)] text-[var(--bg-primary)] border-[var(--text-primary)]" : "border-black/20 text-[var(--text-secondary)] hover:border-black/50"}`}>
                    {m} — {m === "Express" ? "£9.95" : subtotal >= 150 ? "Free" : "£5.95"}
                  </button>
                ))}
              </div>
            </div>
          </form>

          <div className="lg:col-span-2 bg-[var(--bg-secondary)] p-8 h-fit">
            <h2 className="font-serif text-2xl mb-6">Order summary</h2>
            <div className="space-y-3 mb-5">
              {items.map((i) => (
                <div key={i.id} className="flex justify-between text-sm"><span className="text-[var(--text-secondary)]">{i.name} × {i.qty}</span><span>{money(i.price * i.qty)}</span></div>
              ))}
            </div>
            <div className="space-y-2 pt-4 border-t border-black/10 font-mono text-[12px] tracking-[0.08em] uppercase text-[var(--text-secondary)]">
              <div className="flex justify-between"><span>Subtotal</span><span>{money(subtotal)}</span></div>
              <div className="flex justify-between"><span>Shipping</span><span>{ship === 0 ? "Free" : money(ship)}</span></div>
              <div className="flex justify-between"><span>VAT (incl.)</span><span>{money(vat)}</span></div>
              <div className="flex justify-between text-[var(--text-primary)] text-base pt-3 border-t border-black/10"><span>Total</span><span data-testid="co-total">{money(total)}</span></div>
            </div>
            <button data-testid="co-pay" onClick={submit} disabled={loading}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[var(--text-primary)] text-[var(--bg-primary)] h-14 font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-[var(--brass)] transition-colors disabled:opacity-50">
              {loading ? <><CircleNotch size={16} className="animate-spin" /> Redirecting…</> : "Pay Securely"}
            </button>
            <p className="mt-3 font-mono text-[10px] text-[var(--text-muted)] text-center">Card · Apple Pay · Google Pay via Stripe. Your card details never touch our servers.</p>
          </div>
        </section>
      </main>
    </>
  );
}
